import {
  SessionSource,
  SessionResult,
  LifelineType,
  SessionActionResult,
  RevenueType,
} from "./index";
import { ChartOptions, ChartType } from "chart.js";
import { Color } from "ng2-charts";
import * as moment from 'moment';
import { ReportTypes } from "../reports/models/reports";

export const DashboardDateRanges = {
  Today: [moment(), moment()],
  Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
  'Past 7 Days': [moment().subtract(7, 'days'), moment()],
  'Month to Date': [moment().startOf('month'), moment()],
  'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
  'Last 30 days': [moment().subtract(30, 'days'), moment()],
  'Last 3 Months to Date': [moment().subtract(3, 'months').startOf('month'), moment()],
  'Last 6 Months to Date': [moment().subtract(6, 'months').startOf('month'), moment()],
  'Last 12 Months to Date': [moment().subtract(12, 'months').startOf('month'), moment()],
  'Year to Date': [moment().startOf('year'), moment()],
  'Last Year': [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year')],
};

export const DailyDataThreshold = 21;

export interface GroupedChartSeries {
  name: string;
  value: number;
}

export interface GroupedChartRow {
  name: string;
  series: GroupedChartSeries[];
  requests?: number;
}

export enum RetentionSegment {
  Saved = 'Saved',
  Cancelled = 'Cancelled',
  Abandoned = 'Abandoned',
  Other = 'Other',
}

export const stackedBarLegendItems: { label: string; icon: string }[] = [
  { label: RetentionSegment.Saved, icon: '/assets/IconsLibrary/save-icon.svg' },
  { label: RetentionSegment.Cancelled, icon: '/assets/IconsLibrary/cancel-icon.svg' },
  { label: RetentionSegment.Abandoned, icon: '/assets/IconsLibrary/abondoned-icon.svg' },
];

export const retentionTooltipIcons: { [label: string]: string } = {
  [RetentionSegment.Saved]: '/assets/IconsLibrary/save-icon.svg',
  [RetentionSegment.Cancelled]: '/assets/IconsLibrary/cancel-icon.svg',
  [RetentionSegment.Abandoned]: '/assets/IconsLibrary/abondoned-icon.svg',
  [RetentionSegment.Other]: '/assets/IconsLibrary/other-icon.svg',
};

export enum DashboardSections {
  Overview = "OVERVIEW",
  Retention = "RETENTION",
  Marketplace = "MARKETPLACE",
  Phone = "PHONE",
  Subscription = "SUBSCRIPTION",
  Order = "ORDER",
  Return = "RETURN",
  Resolve = "RESOLVE",
}

export const DashboardSectionLabels: { [key in DashboardSections]?: string } = {
  [DashboardSections.Subscription]: "Subscriptions",
  [DashboardSections.Order]: "Orders",
  [DashboardSections.Resolve]: "Overview",
};

// Sections whose data is not fully accurate yet, so their tab carries a (BETA) tag.
export const DashboardBetaSections: DashboardSections[] = [DashboardSections.Subscription, DashboardSections.Order];

export const DashboardAdminOnlySections: DashboardSections[] = [DashboardSections.Resolve];

// The product dashboard page each section lives on; `tab` picks the section there.
export const DashboardSectionPage: { [key in DashboardSections]?: { path: string; tab?: string } } = {
  [DashboardSections.Retention]: { path: "/dashboard/retention" },
  [DashboardSections.Subscription]: { path: "/dashboard/retention", tab: "subscription" },
  [DashboardSections.Order]: { path: "/dashboard/retention", tab: "order" },
  [DashboardSections.Return]: { path: "/dashboard/returns" },
};

// navigateToReport() report-path segment for each section that has its own retention-report-v3
// route; sections without one (Overview, Retention, Marketplace, Phone) fall back to 'order'.
export const DashboardReportSection: { [key in DashboardSections]?: string } = {
  [DashboardSections.Subscription]: "subscription",
  [DashboardSections.Order]: "order",
  [DashboardSections.Return]: "return",
};

// loadProductOptions() route/reportType per section; missing entries fall back to order_dashboard.
export const DashboardProductFilterRoute: {
  [key in DashboardSections]?: { route: string; reportType: ReportTypes };
} = {
  [DashboardSections.Subscription]: {
    route: "subscription_dashboard",
    reportType: ReportTypes.SUBSCRIPTION_RETENTION,
  },
  [DashboardSections.Return]: {
    route: "returns_dashboard",
    reportType: ReportTypes.RETURN_RETENTION,
  },
};

// loadStageOptions() route/reportType per section; Overview merges all three.
export const DashboardStageFilterRoute: {
  [key in DashboardSections]?: { route: string; reportType: ReportTypes };
} = {
  [DashboardSections.Order]: {
    route: "order_dashboard",
    reportType: ReportTypes.ORDER_RETENTION,
  },
  [DashboardSections.Subscription]: {
    route: "subscription_dashboard",
    reportType: ReportTypes.SUBSCRIPTION_RETENTION,
  },
  [DashboardSections.Return]: {
    route: "returns_dashboard",
    reportType: ReportTypes.RETURN_RETENTION,
  },
};

export interface TopData {
  session_date: string;
  source: SessionSource;
  num_sessions: number;
}

export interface SessionCount {
  source: SessionSource;
  result: SessionResult;
  num_sessions: number;
}

export interface Revenue {
  revenue_type: RevenueType;
  total_revenue: number;
}

export interface FaqResults {
  total_up_votes: number;
  total_down_votes: number;
}

export interface LifelineCount {
  lifeline_type: LifelineType;
  num_lifelines: number;
}

export interface SessionActionCount {
  result: SessionActionResult;
  session_date: string;
  num_sessions: number;
}

export interface Dashboard {
  top: TopData[];
  session_counts: SessionCount[];
  revenues: Revenue[];
  faq_results: FaqResults;
  lifelines: LifelineCount[];
  actions: SessionActionCount[];
}

export interface DashboardChartDataset {
  backgroundColor: Array<string>;
  hoverBorderColor: Array<string>;
  data: Array<any>;
  borderWidth: number;
}

export interface DashboardSessionsChartData {
  chartLegend: boolean;
  labels: Array<string>;
  dataset: DashboardChartDataset;
  self_service_rate: Array<number>;
}

export interface DashboardRevenuesChartData {
  title: string;
  labels: Array<string>;
  dataset: DashboardChartDataset;
  reportName: string;
}

export interface BarChartData {
  label: string;
  data: Array<any>;
}

export interface DashboardOrderOperationsChartData {
  title: string;
  datasets: BarChartData[];
  colors: Color[];
  labels: Array<string>;
  options: ChartOptions;
  plugins: Array<any>;
  legend: boolean;
  type: ChartType;
  reportName: string;
}

export enum TimeData {
  DefaultFormat = "Y-M-D HH:mm:ss",
  SessionListDateFormat = "M/D LT",
  ReportsApiDateFormat = "Y-MM-DD hh:mm:ss A",
  ReportsDisplayDateFormat = "Y-M-D",
}

export interface SessionFilters {
  includeTestSessions: boolean;
  includeHangups: boolean;
  includeUnknowns: boolean;
}

/* Dashboard V2 */
export interface DashboardFilter {
  start_date: string;
  end_date: string;
  compare_start_date?: string;
  compare_end_date?: string;
  campaign_id?: string;
  crm_id?: string;
  includeTest?: boolean;
  includeHangups?: boolean;
  includeUnknowns?: boolean;
  timezone: string;
  account_id?: string;
  section?: DashboardSections;
  offer_intent?: number;
  exclude_abandons?: boolean;
  exclude_prefulfillment_cancels?: boolean;
  product_name?: string;
  product_id?: number;
  portal_session_types?: number[];
  merchant_profile_id?: string;
}
export interface CanceledWithPendingOrderResponse {
  quantity: number;
  value_lost: number;
  pct_of_cancellations: number;
  cycle_1: { quantity: number; value: number };
  cycle_2_plus: { quantity: number; value: number };
  avg_value: number;
}

export interface DashboardNavFilters {
  start_date?: string;
  end_date?: string;
  campaign_id?: string;
  offer_intent?: number;
  product_id?: number;
  product_name?: string;
  exclude_abandons?: boolean;
  exclude_prefulfillment_cancels?: boolean;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

interface Filters {
  campaign_id?: number[];
  crm_id?: number;
  offer_intent?: number;
  session_type?: number[];
  product_name?: string;
  cancel_reason?: string;
  '!cancel_reason'?: string[];
}

export interface DashboardReportsFilter {
  fields: string[];
  date_range: DateRange;
  compare_date_range?: DateRange;
  accounts?: number[];
  timezone: string;
  date_filter_type: string;
  filters?: Filters;
  report_version?: number;
  report_type?: number;
  group?: string;
  portal_action_scope?: PortalActionScope;
}

interface InfoCardData {
  title: string;
  hint?: string;
  value: string | null;
  percentage?: number | null;
  is_negative?: boolean;
  subtitle?: string;
  subtitleLoading?: boolean;
  subtitleItems?: { label: string; value: string }[];
}

interface DataVisualizationCardData {
  title: string;
  subtitle: string;
  hint?: string;
  name?: string;
}

interface EngagementCardData {
  title: string;
  count: string | null;
  icon: EngagementCardIcons;
  hint?: string;
}

export enum EngagementCardIcons {
  Phone = 'PHONE',
  Email = 'EMAIL',
  Link = 'LINK',
  Message = 'MESSAGE',
}

interface DashboardChartRecord {
  name: string;
  value?: number | null;
  type?: string;
  sessions?: string | number;
  saved?: number;
}

export type InfoCards = InfoCardData[];
export type DataVisualizationCards = DataVisualizationCardData[];
export type EngagementCards = EngagementCardData[];
export type DashboardChartData = DashboardChartRecord[];

/* API Responses */
export interface DashboardFAQ {
  question: string;
  views: number;
}

export type DashboardTopFAQsAPIResponse = DashboardFAQ[] | {};

export interface DashboardTrafficSource {
  tracking_source: string;
  session_count: number;
}

export type DashboardTrafficSourcesAPIResponse = DashboardTrafficSource[] | {};

export interface CustomerSupportTopIntent {
  intent_name: string;
  session_count: number;
  share: number;
}

export interface CustomerSupportOverview {
  session_count: number;
  unique_sessions: number;
  started_sessions: number;
  all_started_sessions: number;
  inquiries: number;
  abandoned: number;
  track_order_requests: number;
  phone_sessions: number;
  email_support: number;
  direct_link_sessions: number;
  social_sessions: number;
}

export interface CustomerSupportOverviewTrend {
  [field: string]: number | null;
}

export interface CustomerSupportPortalMoment {
  moment_id: number;
  moment_name: string;
  session_count: number;
  share: number;
}

export enum PortalActionType {
  DelayShipment = "delay_shipment",
  ChangeFrequency = "change_frequency",
  ProductSwap = "product_swap",
  UpdatePayment = "update_payment",
}

export enum PortalActionScope {
  Subscription = "subscription",
  Order = "order",
}

export interface CustomerSupportPortalActionBreakdown {
  value: string;
  action_count: number;
  action_share: number;
  session_count: number;
  session_share: number;
}

export interface CustomerSupportPortalAction {
  action_type: PortalActionType;
  action_name: string;
  action_count: number;
  action_share: number;
  session_share: number;
  breakdown: CustomerSupportPortalActionBreakdown[];
}

export interface CustomerSupportPortalActionsResponse {
  results: CustomerSupportPortalAction[];
  pending?: boolean;
  reason?: string;
}

export interface CustomerSupportMarketplaceSummary {
  revenue: number;
  aov: number;
  completed_orders: number;
  conversion_rate: number;
  trend: CustomerSupportOverviewTrend;
}

export interface CustomerSupportTopFaq {
  faq_id: number;
  question: string;
  session_count: number;
  share: number;
}

export interface CustomerSupportTopFaqsResponse {
  results: CustomerSupportTopFaq[];
  pending?: boolean;
  reason?: string;
}

export interface CustomerSupportSummary {
  overview: CustomerSupportOverview;
  overview_trend: CustomerSupportOverviewTrend;
  top_intents: CustomerSupportTopIntent[];
  portal_moments: CustomerSupportPortalMoment[];
  portal_actions: CustomerSupportPortalActionsResponse;
  marketplace_summary: CustomerSupportMarketplaceSummary;
  top_faqs: CustomerSupportTopFaqsResponse;
}

export type CustomerSupportSummaryAPIResponse = CustomerSupportSummary | {};

export interface OverviewRefundsSummary {
  item_refund_amount: number;
  item_refund_quantity: number;
  item_full_refund_amount: number;
  refund_ratio: number;
}

export type OverviewRefundsSummaryAPIResponse = OverviewRefundsSummary[] | {};

export interface DashboardCampaignBreakdown {
  crm_campaign_id: string;
  crm_campaign_name: string;
  session_count: number;
  diff_percentage?: number;
}

export type DashboardTopCampaignsBreakdownAPIResponse =
  | DashboardCampaignBreakdown[]
  | {};

interface DashboardSessionResults {
  Bypass?: number;
  Dropoffs?: number;
  Successful?: number;
  Lifeline?: number;
}
export interface DashboardSessionResultsAPIResponse {
  self_service_rate: [number, number];
  session_results: DashboardSessionResults;
}

// Retention Types
export interface RetentionStatistics {
  save_amount: number;
  save_rate: number;
  hold_save: number;
  hold_cancel: number;
  subscription_save: number;
  subscription_cancel: number;
}

export type DashboardRetentionStatisticsAPIResponse =
  | RetentionStatistics[]
  | {};

export interface RetentionPathBreakdown {
  product_path: string;
  saved: number;
  session_count: number;
}

export type DashboardRetentionPathBreakdownAPIResponse =
  | RetentionPathBreakdown[]
  | {};

export interface RetentionWatchlist {
  reason: string;
  count: number;
  trend: number;
}

export type DashboardRetentionWatchlistAPIResponse = RetentionWatchlist[] | {};

export interface RetentionSurvey {
  cancel_reason: string;
  cancelled: number;
}

export type DashboardRetentionSurveyAPIResponse = RetentionSurvey[] | {};

export interface RetentionDeflectionData {
  key: string;
  count: number;
  diff_percentage: number;
}

export type DashboardRetentionDeflectionDataAPIResponse =
  | RetentionDeflectionData[]
  | {};

export interface RetentionCancelCategory {
  cancel_reason_category: string;
  cancelled: number;
  cancelled_percentage: number;
}

export type DashboardRetentionCancelCategoriesAPIResponse =
  | RetentionCancelCategory[]
  | {};

export interface RetentionDownsellData {
  step_category: string;
  downsell: number;
  downsell_attempt: number;
  downsell_take_rate: number;
  diff_percentage?: number;
}

export type RetentionDownsellAPIResponse = RetentionDownsellData[] | {};

export interface RetentionSummaryData {
  extra_for_grouping: number;
  deflected: number;
  cancelled: number;
  saved: number;
  return_quantity: number;
  dropoffs: number;
  total_saves: number;
  retracted_save: number;
}

export type RetentionSummaryAPIResponse = RetentionDownsellData[] | {};

// Marketplace types
export interface DashboardMarketplaceWatchlist {
  total_cart_abandoned: number;
  total_cart_abandoned_trend?: number;
  total_card_declined: number;
  total_card_declined_trend?: number;
  in_cart: number;
  in_cart_trend?: number;
}

export type DashboardMarketplaceWatchlistAPIResponse =
  | DashboardMarketplaceWatchlist
  | {};

export interface MarketplaceVisitor {
  total_marketplace_views: number;
  created: string;
}
export type DashboardMarketplaceTotalVisitorsAPIResponse =
  | MarketplaceVisitor[]
  | {};

export interface MarketplaceTopCards {
  total_revenue: number;
  total_checked_outs: number;
  average_order_value: number;
  conversion_based_on_visits: number;
}
export type DashboardMarketplaceTopCardsAPIResponse = MarketplaceTopCards | {};

export interface MarketplaceSummary {
  total_checked_outs: number;
  total_cart_abandoned: number;
  total_card_declined: number;
}
export type DashboardMarketplaceSummaryAPIResponse = MarketplaceSummary | {};

export interface MarketplaceSales {
  tracking_source: string | null;
  total_sale: number;
}
export type DashboardMarketplaceSalesAPIResponse = MarketplaceTopCards[] | {};

export interface MarketplaceProductConversion {
  product_name: string;
  conversion_based_on_visits: number;
  total_checked_outs: number;
  total_revenue: number;
  total_views: number;
  trend: number;
}
export type DashboardMarketplaceProductConversionsAPIResponse =
  | MarketplaceProductConversion[]
  | {};

export interface MarketplaceCampaign {
  crm_campaign_name: string;
  conversion_based_on_visits: number;
  trend?: number;
}
export type DashboardMarketplaceCampaignAPIResponse =
  | MarketplaceCampaign[]
  | {};

export interface RetentionMiscDataAPIResponse {
  customers_saved: number;
  customers_cancelled: number;
  path_breakdown: RetentionPathBreakdown[] | [];
  survey_responses: RetentionSurvey[] | [];
  cancel_categories: RetentionCancelCategory[] | [];
}

// V2 Dashboard API Response Types (ClickHouse)

export enum KpiCardKey {
  CancelRequests = 'cancel_requests',
  Saved = 'saved',
  SavedRevenue = 'saved_revenue',
  Cancelled = 'cancelled',
  Abandoned = 'abandoned',
  RetentionRate = 'retention_rate',
  CanceledWithOrder = 'sub_canceled_with_order',
}

export enum MoneyCardKey {
  TotalBilled = 'total_billed',
  TotalRefunds = 'total_refunds',
  PartialRefunds = 'partial_refunds',
  FullRefunds = 'full_refunds',
  AvgRefund = 'avg_refund',
  RevenueSaved = 'revenue_saved',
  RevenueAbandoned = 'revenue_abandoned',
  NetRevenue = 'net_revenue',
  // Returns (RMA) only - money_cards appends these two cards for report_type RETURN_RETENTION.
  CreditsIssued = 'credits_issued',
  CreditsAvg = 'credits_avg',
  ReturnsRefundAmount = 'returns_refund_amount',
  ReturnsRefundRate = 'returns_refund_rate',
}

export interface KpiCard {
  key: KpiCardKey;
  label: string;
  value: number;
  sub_value: number | null;
  pct: number | null;
  trend_pct: number | null;
  previous_value: number | null;
}
export interface KpiCardsResponse { cards: KpiCard[] }

export interface RetentionSummaryV2Response {
  items_saved: number;
  items_cancelled: number;
  items_abandoned: number;
}

export interface CancellationReasonRow { reason: string; requests: number; cancelled: number; abandoned: number; saved: number; value: number }
export interface TopCancellationReasonsResponse { results: CancellationReasonRow[] }

export interface MoneyCard {
  key: MoneyCardKey;
  label: string;
  amount: number;
  pct?: number | null;
  trend_pct?: number | null;
  previous_value?: number | null;
}
export interface MoneyCardsResponse { cards: MoneyCard[] }

export interface PerformanceRow {
  dimension: string | number;
  dimension_id?: string | number | null;
  requests: number;
  impressions?: number;
  cancelled: number;
  abandoned: number;
  saves: number;
  save_pct: number;
  value: number;
}
export interface PerformanceTableResponse {
  results: PerformanceRow[];
  summary_row?: { requests: number; cancelled: number; abandoned: number; saves: number; save_pct: number };
  pending?: boolean;
  reason?: string;
}

export interface OfferTypeRow { offer_type: number; impressions: number; requests: number; saves: number; save_pct: number }
export interface OfferTypeResponse { results: OfferTypeRow[] }

// Returns Dashboard - endpoints unique to ReturnDashboardViewSet (OP-1554)

export interface OpenReturnActivityResponse {
  pending_returns: number;
  pending_value: number;
  expired_returns: number;
  expired_value: number;
  pending?: boolean;
  reason?: string;
}

export interface ReturnsOfferApiRow {
  offer_name: string;
  offer_type: number;
  offer_id: number;
  total_impressions: number;
  total_accepted: number;
  accepted_pct: number;
  avg_mrr_saved: number;
  total_saved: number;
}
export interface TopOffersResponse { results: ReturnsOfferApiRow[] }

// Matches config_v2.py AI_SUMMARY_RULES rule_key values exactly.
export enum ReturnsAiInsightRule {
  TopDeflectedProduct = 'top_deflected_product',
  WorstReturnReason = 'worst_return_reason',
  DeflectionTrend = 'deflection_trend',
  CreditsCost = 'credits_cost',
  ExpiredReturnsFlag = 'expired_returns_flag',
}

export interface AiInsightApiRow { rule: ReturnsAiInsightRule; text: string }
export interface AiInsightsResponse { insights: AiInsightApiRow[]; suppressed: any[]; status: string }

export enum DayOfWeek {
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Sunday = 7,
}

export const DayOfWeekLabels: { [key: number]: string } = {};
DayOfWeekLabels[DayOfWeek.Monday] = 'Monday';
DayOfWeekLabels[DayOfWeek.Tuesday] = 'Tuesday';
DayOfWeekLabels[DayOfWeek.Wednesday] = 'Wednesday';
DayOfWeekLabels[DayOfWeek.Thursday] = 'Thursday';
DayOfWeekLabels[DayOfWeek.Friday] = 'Friday';
DayOfWeekLabels[DayOfWeek.Saturday] = 'Saturday';
DayOfWeekLabels[DayOfWeek.Sunday] = 'Sunday';

export const ReturnsAiInsightIconMap: Record<ReturnsAiInsightRule, { icon: string; iconBg: string }> = {
  [ReturnsAiInsightRule.TopDeflectedProduct]: { icon: '/assets/IconsLibrary/ai-insights-deflection-icon.svg', iconBg: '#DBF3E6' },
  [ReturnsAiInsightRule.WorstReturnReason]: { icon: '/assets/IconsLibrary/ai-insights-icon.svg', iconBg: '#E5EDFF' },
  [ReturnsAiInsightRule.DeflectionTrend]: { icon: '/assets/IconsLibrary/ai-insights-deflection-icon.svg', iconBg: '#DBF3E6' },
  [ReturnsAiInsightRule.CreditsCost]: { icon: '/assets/IconsLibrary/ai-insights-credits-icon.svg', iconBg: '#E8DFFE' },
  [ReturnsAiInsightRule.ExpiredReturnsFlag]: { icon: '/assets/IconsLibrary/ai-insights-icon.svg', iconBg: 'rgba(217, 83, 79, 0.1)' },
};

// Subscription Dashboard Types
export interface SubscriptionTopCards {
  cancel_requests: number;
  cancel_requests_trend: number;
  cancel_requests_percentage: number;
  cancelled: number;
  cancelled_trend: number;
  cancelled_percentage: number;
  abandoned: number;
  abandoned_trend: number;
  abandoned_percentage: number;
  retention_rate: number;
  retention_rate_trend: number;
}
export type SubscriptionTopCardsAPIResponse = SubscriptionTopCards | {};

export interface SubscriptionSummaryData {
  saved: number;
  cancelled: number;
  abandoned: number;
  other: number;
}
export type SubscriptionSummaryAPIResponse = SubscriptionSummaryData | {};

export interface SubscriptionGroupedBarData {
  name: string;
  saved: number;
  cancelled: number;
  total_requests: number;
}
export type SubscriptionCancellationReasonsAPIResponse = SubscriptionGroupedBarData[] | {};
export type SubscriptionBillingCycleAPIResponse = SubscriptionGroupedBarData[] | {};
export type SubscriptionFrequencyAPIResponse = SubscriptionGroupedBarData[] | {};

export interface SubscriptionPerformanceItem {
  name: string;
  requests: number;
  saves: number;
  save_rate: number;
}
export type SubscriptionOfferTypeAPIResponse = SubscriptionPerformanceItem[] | {};
export type SubscriptionProductAPIResponse = SubscriptionPerformanceItem[] | {};
export type SubscriptionCampaignAPIResponse = SubscriptionPerformanceItem[] | {};
export type SubscriptionCustomerStageAPIResponse = SubscriptionPerformanceItem[] | {};
